import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { campaignAPI, productAPI } from "@/services/api";
import { isValidProductId } from "@/lib/productId";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Megaphone,
  PauseCircle,
  Plus,
  RefreshCw,
  Search,
  Target,
  TrendingUp,
  Users,
  Wallet,
  XCircle,
} from "lucide-react";
import {
  TailAdminDataTable,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminProgressRow,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const statusMeta = {
  draft: { label: "Draft", tone: "neutral", icon: Clock },
  active: { label: "Active", tone: "success", icon: CheckCircle2 },
  paused: { label: "Paused", tone: "warning", icon: PauseCircle },
  ended: { label: "Ended", tone: "danger", icon: XCircle },
};

const goalLabels = {
  subscription: "Subscription",
  download: "Download",
  registration: "Registration",
  purchase: "Purchase",
};

const getCampaignId = (campaign) => campaign?.campaign_id || campaign?.id || "";
const getCampaignProductId = (campaign) => campaign?.product_id || campaign?.productId || "";
const formatCurrency = (amount) => `NGN ${Number(amount || 0).toLocaleString()}`;

const getBudgetProgress = (campaign) => {
  const cap = Number(campaign?.budget_cap || 0);
  if (!cap) return 0;
  return Math.min(100, Math.round((Number(campaign?.budget_spent || 0) / cap) * 100));
};

const ClientCampaigns = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const scopedToProduct = isValidProductId(productId);
  const [campaigns, setCampaigns] = useState([]);
  const [productName, setProductName] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadError, setLoadError] = useState("");

  const createCampaignPath = scopedToProduct
    ? `/client/products/${productId}/campaigns/create`
    : "/client/campaigns/create";

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const listOptions = scopedToProduct
        ? { product_id: productId, pageSize: 100 }
        : { pageSize: 100 };
      const response = await campaignAPI.listCampaigns(listOptions);
      let nextCampaigns = response.campaigns || [];

      if (scopedToProduct) {
        nextCampaigns = nextCampaigns.filter(
          (campaign) => getCampaignProductId(campaign) === productId
        );
      }

      setCampaigns(nextCampaigns);

      if (scopedToProduct) {
        try {
          const productRes = await productAPI.getProduct(productId);
          const product = productRes.data?.product || productRes.data;
          setProductName(product?.name || "");
        } catch {
          setProductName("");
        }
      } else {
        setProductName("");
      }
    } catch (error) {
      const detail = error.response?.status === 404
        ? "Campaign API is not available on the connected backend yet."
        : "Failed to load campaigns.";
      setLoadError(detail);
      setCampaigns([]);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, [productId, scopedToProduct]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handlePublish = async (campaignId) => {
    setPublishing(campaignId);
    try {
      await campaignAPI.publishCampaign(campaignId);
      toast.success("Campaign published");
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish campaign");
    } finally {
      setPublishing(null);
    }
  };

  const statusCounts = useMemo(() => ({
    all: campaigns.length,
    draft: campaigns.filter((campaign) => campaign.status === "draft").length,
    active: campaigns.filter((campaign) => campaign.status === "active").length,
    paused: campaigns.filter((campaign) => campaign.status === "paused").length,
    ended: campaigns.filter((campaign) => campaign.status === "ended").length,
  }), [campaigns]);

  const filteredCampaigns = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      const matchesStatus = filter === "all" || campaign.status === filter;
      const matchesSearch = !query
        || campaign.title?.toLowerCase().includes(query)
        || campaign.description?.toLowerCase().includes(query)
        || campaign.goal_type?.toLowerCase().includes(query);
      return matchesStatus && matchesSearch;
    });
  }, [campaigns, filter, searchQuery]);

  const summary = useMemo(() => {
    const budget = campaigns.reduce((sum, campaign) => sum + Number(campaign.budget_cap || 0), 0);
    const spent = campaigns.reduce((sum, campaign) => sum + Number(campaign.budget_spent || 0), 0);
    const participants = campaigns.reduce((sum, campaign) => sum + Number(campaign.participation_count || 0), 0);
    const avgCommission = campaigns.length
      ? campaigns.reduce((sum, campaign) => sum + Number(campaign.commission_per_goal || 0), 0) / campaigns.length
      : 0;

    return { budget, spent, participants, avgCommission };
  }, [campaigns]);

  const campaignRows = filteredCampaigns.map((campaign) => {
    const campaignId = getCampaignId(campaign);
    const meta = statusMeta[campaign.status] || statusMeta.draft;
    const progress = getBudgetProgress(campaign);

    return {
      id: campaignId,
      cells: [
        <div>
          <p className="font-semibold text-gray-900">{campaign.title}</p>
          <p className="mt-1 max-w-md text-xs leading-5 text-gray-500">
            {campaign.description || "No description"}
          </p>
        </div>,
        <TailAdminStatusBadge tone={meta.tone}>{meta.label}</TailAdminStatusBadge>,
        <span className="font-medium text-gray-700">
          {goalLabels[campaign.goal_type] || campaign.goal_type || "Goal"}
        </span>,
        <span className="font-semibold text-emerald-600">
          {formatCurrency(campaign.commission_per_goal)}
        </span>,
        <span className="inline-flex items-center gap-1 text-gray-600">
          <Users className="h-4 w-4 text-gray-400" />
          {Number(campaign.participation_count || 0).toLocaleString()}
        </span>,
        <div className="min-w-[180px]">
          {campaign.budget_cap ? (
            <TailAdminProgressRow
              label={formatCurrency(campaign.budget_spent)}
              helper={`of ${formatCurrency(campaign.budget_cap)}`}
              value={progress}
              tone={progress >= 90 ? "danger" : progress >= 70 ? "warning" : "primary"}
            />
          ) : (
            <span className="text-sm text-gray-500">No cap</span>
          )}
        </div>,
        <div className="flex min-w-[190px] items-center gap-2">
          {campaign.status === "draft" ? (
            <Button
              size="sm"
              className="h-8 rounded-lg"
              disabled={publishing === campaignId}
              onClick={() => handlePublish(campaignId)}
            >
              {publishing === campaignId ? <RefreshCw className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />}
              Publish
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-lg"
            onClick={() => navigate(`/client/campaigns/${campaignId}`)}
          >
            Details
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>,
      ],
    };
  });

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow={scopedToProduct ? "Product campaigns" : "Campaign control"}
        title={scopedToProduct ? productName || "Prove-Requests" : "Campaigns"}
        description={
          scopedToProduct
            ? "Campaigns and prove-requests tied to this offer."
            : "Launch and monitor BRM campaigns tied to your Prove-Requests."
        }
        actions={
          <>
            {scopedToProduct ? (
              <Button
                variant="outline"
                className="h-10 rounded-lg"
                onClick={() => navigate("/client/products")}
              >
                Back to offers
              </Button>
            ) : null}
            <Button
              variant="outline"
              className="h-10 rounded-lg"
              onClick={loadCampaigns}
              disabled={loading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button className="h-10 rounded-lg" onClick={() => navigate(createCampaignPath)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Campaign
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard
          label="Total campaigns"
          value={campaigns.length}
          icon={Megaphone}
          tone="primary"
          helper="All statuses"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Active campaigns"
          value={statusCounts.active}
          icon={TrendingUp}
          tone="success"
          helper="Promoted now"
          loading={loading}
        />
        <TailAdminMetricCard
          label="BRM participants"
          value={summary.participants.toLocaleString()}
          icon={Users}
          tone="warning"
          helper="Across campaigns"
          loading={loading}
        />
        <TailAdminMetricCard
          label="Avg commission"
          value={formatCurrency(summary.avgCommission)}
          icon={Wallet}
          tone="neutral"
          helper="Per conversion"
          loading={loading}
        />
      </div>

      <TailAdminPanel
        title="Campaign inventory"
        description="Filter campaigns by lifecycle status, budget progress, and BRM participation."
        action={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -trangray-y-1/2 text-gray-400" />
            <input
              className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-brand-500 sm:w-72"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>
        }
      >
        <div className="mb-5 flex flex-wrap gap-2">
          {[
            ["all", "All", statusCounts.all],
            ["draft", "Draft", statusCounts.draft],
            ["active", "Active", statusCounts.active],
            ["paused", "Paused", statusCounts.paused],
            ["ended", "Ended", statusCounts.ended],
          ].map(([key, label, count]) => (
            <button
              key={key}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                filter === key
                  ? "border-brand-500 bg-brand-500 text-white"
                  : "border-gray-200 bg-white text-gray-600 hover:border-brand-500/40 hover:text-gray-900"
              }`}
              onClick={() => setFilter(key)}
            >
              {label} ({count})
            </button>
          ))}
        </div>

        {loading || filteredCampaigns.length > 0 ? (
          <TailAdminDataTable
            loading={loading}
            columns={["Campaign", "Status", "Goal", "Commission", "BRMs", "Budget", "Actions"]}
            rows={campaignRows}
          />
        ) : (
          <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-8 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
              <Target className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-semibold text-gray-950">
              {loadError ? "Campaigns are not available yet" : "No campaigns found"}
            </h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-gray-500">
              {loadError || (filter === "all"
                ? "Create your first campaign to start attracting BRMs."
                : `No ${filter} campaigns match your current view.`)}
            </p>
            <Button className="mt-5 rounded-lg" onClick={() => navigate(createCampaignPath)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Campaign
            </Button>
          </div>
        )}
      </TailAdminPanel>
    </div>
  );
};

export default ClientCampaigns;
